/**
 * ログインページ
 * ユーザー認証とセッション管理
 */

import { Button } from "@/shared/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/shared/ui/card"
import { Input } from "@/shared/ui/input"
import { Label } from "@/shared/ui/label"
import { createClient } from '@/shared/api/supabase/client'
import React, { Suspense, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from "sonner"

const DASHBOARD_URL = '/'

/**
 * ログインフォームコンポーネント
 */
function LoginForm() {
    const router = useNavigate()
    const [searchParams] = useSearchParams()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [redirectUrl, setRedirectUrl] = useState<string>(DASHBOARD_URL)

    useEffect(() => {
        const redirectParam = searchParams.get('redirect')
        const storedRedirect = localStorage.getItem('redirectAfterLogin')

        if (redirectParam) {
            setRedirectUrl(redirectParam)
            console.log('Redirect URL from params:', redirectParam)
        } else if (storedRedirect) {
            setRedirectUrl(storedRedirect)
            console.log('Redirect URL from storage:', storedRedirect)
            localStorage.removeItem('redirectAfterLogin')
        }
    }, [searchParams])

    const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setIsLoading(true)

        if (!email || !password) {
            toast.error('メールアドレスとパスワードを入力してください。')
            setIsLoading(false)
            return
        }

        const supabase = createClient()

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            })

            if (error) {
                console.error('Login error:', error)
                toast.error(`ログインに失敗しました: ${error.message}`)
                setIsLoading(false)
                return
            }

            toast.success('ログインに成功しました！')

            setTimeout(() => {
                router(redirectUrl)
            }, 100)
        } catch (e: any) {
            console.error('Login exception:', e)
            toast.error(e.message || '予期せぬエラーが発生しました。')
            setIsLoading(false)
        }
    }

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
            <Card className="w-full max-w-sm">
                <CardHeader className="space-y-1 text-center">
                    <CardTitle className="text-2xl">ログイン</CardTitle>
                    <CardDescription>
                        メールアドレスとパスワードを入力してください
                    </CardDescription>
                    <div className="text-xs text-gray-500 mt-2">
                        <p>開発用アカウント:</p>
                        <p>管理者: admin@example.com / Admin1234!</p>
                        <p>一般ユーザー: user@example.com / User1234!</p>
                    </div>
                </CardHeader>
                <form onSubmit={handleLogin}>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">メールアドレス</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="mail@example.com"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">パスワード</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isLoading}
                            />
                        </div>
                    </CardContent>
                    <CardFooter>
                        <Button type="submit" className="w-full" disabled={isLoading}>
                            {isLoading ? 'ログイン中...' : 'ログイン'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    )
}

/**
 * ログインページコンポーネント
 */
export default function LoginPage() {
    return (
        <Suspense fallback={
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">ページを読み込み中...</p>
                </div>
            </div>
        }>
            <LoginForm />
        </Suspense>
    )
}
